/**
 * Stable sortable instant for a vitals row (local date + time fields from API).
 * Used to pick practice vs patient target mode at reading time.
 */
export const getMeasurementEpochMs = (measurement) => {
  if (!measurement || typeof measurement !== 'object') return Date.now();
  const datePart =
    measurement.measurement_date ||
    (measurement.measure_new_date_time && String(measurement.measure_new_date_time).split('T')[0]) ||
    (measurement.measure_date_time && String(measurement.measure_date_time).split(/\s|T/)[0]) ||
    (measurement.created_at && String(measurement.created_at).split('T')[0]) ||
    '';
  if (!datePart) return Date.now();
  let timePart =
    measurement.measurement_time ||
    (measurement.measure_new_date_time && String(measurement.measure_new_date_time).includes('T')
      ? String(measurement.measure_new_date_time).split('T')[1]?.slice(0, 8)
      : null) ||
    (measurement.measure_date_time && String(measurement.measure_date_time).split(/\s|T/).slice(1).join('T')) ||
    (measurement.created_at && String(measurement.created_at).split('T')[1]?.slice(0, 8)) ||
    '12:00:00';
  timePart = String(timePart).trim();
  const parts = timePart.split(':');
  const hh = String(parts[0] != null && parts[0] !== '' ? parts[0] : '12').padStart(2, '0');
  const mm = String(parts[1] != null && parts[1] !== '' ? String(parts[1]).slice(0, 2) : '00').padStart(2, '0');
  const ssRaw = parts[2] != null ? String(parts[2]).replace(/\D/g, '').slice(0, 2) : '00';
  const ss = String(ssRaw || '00').padStart(2, '0');
  const iso = `${datePart}T${hh}:${mm}:${ss}`;
  const d = new Date(iso);
  const t = d.getTime();
  return Number.isNaN(t) ? Date.now() : t;
};

const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

const isNonEmptyObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;

export const normalizePeriodName = (value) =>
  String(value || '').toLowerCase().replace(/[\s_-]/g, '');

const normalizePeriodLookupKey = (value) =>
  normalizePeriodName(value).replace(/time$/i, '');

export const findPeriodByName = (periods, periodName) => {
  if (!Array.isArray(periods) || periods.length === 0 || !periodName) return null;

  const normalizedInput = normalizePeriodName(periodName);
  const inputLookup = normalizePeriodLookupKey(periodName);

  return (
    periods.find((p) => normalizePeriodName(p?.name) === normalizedInput) ||
    periods.find((p) => normalizePeriodLookupKey(p?.name) === inputLookup) ||
    periods.find((p) => {
      const periodLookup = normalizePeriodLookupKey(p?.name);
      return periodLookup.includes(inputLookup) || inputLookup.includes(periodLookup);
    }) ||
    null
  );
};

export const resolvePeriodNameByIdOrLabel = (periods, measurement) => {
  if (!Array.isArray(periods) || periods.length === 0 || !measurement) return null;

  const periodId = measurement?.period_id != null ? String(measurement.period_id) : null;
  if (periodId) {
    const matchedById = periods.find((p) => String(p.id) === periodId);
    if (matchedById?.name) return matchedById.name;
  }

  const rawLabel = measurement?.period_name || measurement?.period || '';
  const normalizedRaw = normalizePeriodName(rawLabel);
  const normalizedRawLookup = normalizePeriodLookupKey(rawLabel);
  if (!normalizedRaw && !normalizedRawLookup) return null;

  const matchedByLabel =
    periods.find((p) => normalizePeriodName(p.name) === normalizedRaw) ||
    periods.find((p) => normalizePeriodLookupKey(p.name) === normalizedRawLookup) ||
    periods.find((p) => {
      const periodLookup = normalizePeriodLookupKey(p.name);
      return periodLookup.includes(normalizedRawLookup) || normalizedRawLookup.includes(periodLookup);
    });
  return matchedByLabel?.name || null;
};

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const getPeriodMergeKey = (period) => {
  const normalizedName = normalizePeriodName(period?.name);
  if (normalizedName) return `name:${normalizedName}`;
  if (period?.id != null) return `id:${String(period.id)}`;
  return 'unknown';
};

const mergePeriodList = (patientList, practiceList) => {
  const patient = Array.isArray(patientList) ? patientList : [];
  const practice = Array.isArray(practiceList) ? practiceList : [];

  if (patient.length === 0) return practice;
  if (practice.length === 0) return patient;

  const patientByKey = new Map(patient.map((p) => [getPeriodMergeKey(p), p]));
  const usedPatientKeys = new Set();

  const merged = practice.map((basePeriod) => {
    const key = getPeriodMergeKey(basePeriod);
    const patientPeriod = patientByKey.get(key);
    if (!patientPeriod) return basePeriod;

    usedPatientKeys.add(key);
    return {
      ...basePeriod,
      ...patientPeriod,
      startTime: hasValue(patientPeriod.startTime) ? patientPeriod.startTime : (basePeriod.startTime || basePeriod.start || ''),
      endTime: hasValue(patientPeriod.endTime) ? patientPeriod.endTime : (basePeriod.endTime || basePeriod.end || ''),
      start: hasValue(patientPeriod.start) ? patientPeriod.start : (basePeriod.start || basePeriod.startTime || ''),
      end: hasValue(patientPeriod.end) ? patientPeriod.end : (basePeriod.end || basePeriod.endTime || '')
    };
  });

  patient.forEach((patientPeriod) => {
    const key = getPeriodMergeKey(patientPeriod);
    if (!usedPatientKeys.has(key) && !practice.some((p) => getPeriodMergeKey(p) === key)) {
      merged.push(patientPeriod);
    }
  });

  return merged;
};

const mergeTargetList = (patientList, practiceList) => {
  const patient = Array.isArray(patientList) ? patientList : [];
  const practice = Array.isArray(practiceList) ? practiceList : [];

  if (patient.length === 0) return practice;
  if (practice.length === 0) return patient;

  const patientByKey = new Map(patient.map((p) => [getPeriodMergeKey(p), p]));
  const usedPatientKeys = new Set();

  const merged = practice.map((baseTarget) => {
    const key = getPeriodMergeKey(baseTarget);
    const patientTarget = patientByKey.get(key);
    if (!patientTarget) return baseTarget;

    usedPatientKeys.add(key);
    return {
      ...baseTarget,
      ...patientTarget
    };
  });

  patient.forEach((patientTarget) => {
    const key = getPeriodMergeKey(patientTarget);
    if (!usedPatientKeys.has(key) && !practice.some((p) => getPeriodMergeKey(p) === key)) {
      merged.push(patientTarget);
    }
  });

  return merged;
};

export const hasAnyScheduleTargets = (data) => {
  if (!data) return false;
  return (
    isNonEmptyArray(data.dailySchedule) ||
    isNonEmptyArray(data.bloodPressureTimePeriod) ||
    isNonEmptyArray(data.bloodPressureTargetRange) ||
    isNonEmptyArray(data.bloodGlucoseTimePeriod) ||
    isNonEmptyArray(data.bloodGlucoseTargetRange) ||
    isNonEmptyArray(data.weightTimePeriod) ||
    isNonEmptyObject(data.weightTargetRange)
  );
};

// Section-wise precedence:
// - Use patient section if it has data
// - Otherwise fallback to practice section
export const resolveEffectiveScheduleTargets = (patientData, practiceData) => {
  const patient = patientData || {};
  const practice = practiceData || {};

  const effective = {
    ...practice,
    ...patient
  };

  // Strict precedence rule:
  // If patient has data for a section, use ONLY patient data for that section.
  // Do not merge with practice data, to avoid accidental fallback mixing.
  effective.dailySchedule = isNonEmptyArray(patient.dailySchedule)
    ? patient.dailySchedule
    : (practice.dailySchedule || []);

  effective.bloodPressureTimePeriod = isNonEmptyArray(patient.bloodPressureTimePeriod)
    ? patient.bloodPressureTimePeriod
    : (practice.bloodPressureTimePeriod || []);

  effective.bloodPressureTargetRange = isNonEmptyArray(patient.bloodPressureTargetRange)
    ? patient.bloodPressureTargetRange
    : (practice.bloodPressureTargetRange || []);

  effective.bloodGlucoseTimePeriod = isNonEmptyArray(patient.bloodGlucoseTimePeriod)
    ? patient.bloodGlucoseTimePeriod
    : (practice.bloodGlucoseTimePeriod || []);

  effective.bloodGlucoseTargetRange = isNonEmptyArray(patient.bloodGlucoseTargetRange)
    ? patient.bloodGlucoseTargetRange
    : (practice.bloodGlucoseTargetRange || []);

  effective.weightTimePeriod = isNonEmptyArray(patient.weightTimePeriod)
    ? patient.weightTimePeriod
    : (practice.weightTimePeriod || []);

  effective.weightTargetRange = isNonEmptyObject(patient.weightTargetRange)
    ? patient.weightTargetRange
    : (practice.weightTargetRange || {});

  return effective;
};

/**
 * When `usePatientTargets` is false, measurement UIs use practice schedule rows only
 * (`is_practice` = practice in the API). When true, or when auto-resolving for a patient
 * that has custom schedule targets configured, patient rows (`is_practice` = patient)
 * win per section when the patient endpoint returns data.
 */
export const resolveDisplayScheduleTargets = ({ practiceData, patientData, usePatientTargets }) => {
  if (usePatientTargets === false) {
    return resolveEffectiveScheduleTargets({}, practiceData || {});
  }
  if (usePatientTargets === true || hasAnyScheduleTargets(patientData)) {
    return resolveEffectiveScheduleTargets(patientData || {}, practiceData || {});
  }
  return resolveEffectiveScheduleTargets({}, practiceData || {});
};
